import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { cn } from '@/lib/utils';

interface LoadingScreenProps {
  message?: string;
  className?: string;
  animationType?: 'spinner' | 'pulse' | 'default';
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message = 'Loading...',
  className,
  animationType = 'spinner',
}) => {
  const [animationData, setAnimationData] = useState<unknown>(null);

  useEffect(() => {
    const loadAnimation = async () => {
      try {
        let animationPath = '';
        switch (animationType) {
          case 'spinner':
            animationPath = '/lottie/loading-spinner.json';
            break;
          case 'pulse':
            animationPath = '/lottie/loading-pulse.json';
            break;
          default:
            animationPath = '/lottie/loading-spinner.json';
        }

        const response = await fetch(animationPath);
        const data = await response.json();
        setAnimationData(data);
      } catch {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to load Lottie animation, falling back to CSS animation'
        );
        // Fallback to CSS animation if Lottie fails to load
      }
    };

    loadAnimation();
  }, [animationType]);

  return (
    <div
      className={cn(
        'fixed inset-0 bg-white dark:bg-gray-900 flex flex-col items-center justify-center z-50',
        'bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900',
        className
      )}
    >
      <div className="flex flex-col items-center justify-center space-y-6">
        {/* Lottie Animation or Fallback */}
        <div className="relative">
          {animationData ? (
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true}
              className="w-32 h-32"
            />
          ) : (
            // Fallback CSS animation
            <div className="w-32 h-32 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
          )}
          {/* Optional: Add a subtle shadow or glow effect */}
          <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-xl scale-110"></div>
        </div>

        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {message}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please wait while we prepare everything for you
          </p>
        </div>

        {/* Optional: Add a progress indicator */}
        <div className="w-48 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-orange-500 to-amber-600 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Optional: Add some decorative elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full blur-2xl"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-amber-100 dark:bg-amber-900/30 rounded-full blur-2xl"></div>
    </div>
  );
};

// Alternative loading screen with dots animation
export const LoadingDots: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn('flex items-center justify-center space-x-2', className)}
    >
      <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
      <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
      <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"></div>
    </div>
  );
};

// Minimal loading screen for inline use
export const LoadingSpinner: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ size = 'md', className }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-2 border-gray-300 border-t-orange-500',
        sizeClasses[size],
        className
      )}
    ></div>
  );
};
